/**
 * StructuralComparison.tsx — Structure changes
 *
 * Shows how the two charts are built differently: the parts of the account
 * number, hierarchy depth, new details the target asks for, account type
 * alignment, and the candidate matches found before account-level mapping.
 */

import React, { useState, useEffect } from 'react';
import {
  ConversionProject,
  StructuralComparison as ComparisonData,
  runComparison,
  getComparison,
} from '../../services/conversionArchitectAPI';
import {
  c, font, panel, pill, btnPrimary, btnSecondary,
} from './tokens';
import Toast from './Toast';

interface StructuralComparisonProps {
  project: ConversionProject;
  onPhaseComplete: () => void;
}

type TabKey = 'segments' | 'hierarchy' | 'attributes' | 'types' | 'alignments';

const TAB_LABELS: Record<TabKey, string> = {
  segments: 'Parts of the account number',
  hierarchy: 'Levels',
  attributes: 'New details',
  types: 'Account types',
  alignments: 'Matches found',
};

const MATCH_STYLES: Record<string, { bg: string; fg: string; border?: string }> = {
  exact: { bg: c.green.fill, fg: c.green.text },
  exact_code: { bg: c.green.fill, fg: c.green.text },
  exact_name: { bg: c.green.fill, fg: c.green.text },
  fuzzy: { bg: c.blue.fill, fg: c.blue.text },
  partial: { bg: c.amber.fill, fg: c.amber.text, border: c.amber.border },
  unmatched: { bg: c.amber.fill, fg: c.amber.text, border: c.amber.border },
  none: { bg: c.amber.fill, fg: c.amber.text, border: c.amber.border },
  fuzzy_name: { bg: c.blue.fill, fg: c.blue.text },
  new: { bg: c.amber.fill, fg: c.amber.text, border: c.amber.border },
};

const MATCH_LABELS: Record<string, string> = {
  exact: 'Same',
  exact_code: 'Same code',
  exact_name: 'Same name',
  fuzzy: 'Close match',
  fuzzy_name: 'Close match',
  partial: 'Partial match',
  unmatched: 'No match',
  none: 'New part',
  new: 'New',
};

function matchBadge(match: string): React.ReactNode {
  const style = MATCH_STYLES[match] || { bg: c.fill2, fg: c.tableLabel };
  return <span style={pill(style.bg, style.fg, style.border)}>{MATCH_LABELS[match] || match.replace(/_/g, ' ')}</span>;
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: 10,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.8px',
  color: c.textSecondary,
  borderBottom: `1px solid ${c.border}`,
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '9px 10px',
  fontSize: 12.5,
  color: c.textBody,
  borderBottom: `1px solid ${c.hairline}`,
};

const StructuralComparison: React.FC<StructuralComparisonProps> = ({
  project,
  onPhaseComplete,
}) => {
  const [comparison, setComparison] = useState<ComparisonData | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('segments');
  const [toast, setToast] = useState('');

  useEffect(() => {
    loadComparison();
  }, [project.id]);

  const loadComparison = async () => {
    setLoading(true);
    try {
      const data = await getComparison(project.id);
      setComparison(data);
    } catch {
      // No comparison yet — expected
    } finally {
      setLoading(false);
    }
  };

  const handleRunComparison = async () => {
    setRunning(true);
    try {
      const data = await runComparison(project.id);
      setComparison(data);
      setToast('Structure comparison complete');
    } catch (err: any) {
      console.error('Comparison failed:', err);
      setToast(err.response?.data?.error || 'The comparison could not run');
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div style={{ ...panel, fontFamily: font.ui, fontSize: 13, color: c.textSecondary }}>
        Loading the structure comparison…
      </div>
    );
  }

  if (!comparison) {
    return (
      <div style={{ fontFamily: font.ui }}>
        <div style={{ ...panel, textAlign: 'center', padding: '48px 24px' }}>
          <h2 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 800, color: c.textPrimary }}>
            Compare the two structures
          </h2>
          <p style={{ margin: '0 auto 20px', maxWidth: 560, fontSize: 13, color: c.textBody, lineHeight: 1.6 }}>
            See how the parts of the account number line up between the old chart and the new one —
            what's new, what's gone, and where the likely matches are.
          </p>
          <button
            type="button"
            style={{ ...btnPrimary(running), fontSize: 14, padding: '11px 22px' }}
            onClick={handleRunComparison}
            disabled={running}
          >
            {running ? 'Comparing structures…' : 'Compare the structures'}
          </button>
        </div>
        <Toast message={toast} onClose={() => setToast('')} />
      </div>
    );
  }

  const { summary } = comparison;

  // Derive match-method counts client-side — the server summary carries only
  // preliminary_alignment_count; the per-method breakdown lives in the
  // candidate_alignments rows themselves.
  const aligns = comparison.candidate_alignments || [];
  const exactCodeCount = aligns.filter(a => a.match_method === 'exact_code').length;
  const exactNameCount = aligns.filter(a => a.match_method === 'exact_name').length;
  const fuzzyCount = aligns.filter(a => a.match_method === 'fuzzy_name').length;
  const totalAligns = summary.preliminary_alignment_count ?? aligns.length;
  const coverageRate = summary.source_account_count
    ? totalAligns / summary.source_account_count
    : 0;
  const unmatchedCount = Math.max(0, (summary.source_account_count || 0) - totalAligns);

  const summaryCards: Array<{ value: string; label: string; warn?: boolean }> = [
    { value: String(summary.source_account_count), label: 'Old accounts' },
    { value: String(summary.target_account_count), label: 'New accounts' },
    { value: String(totalAligns), label: 'Matches found' },
    { value: `${(coverageRate * 100).toFixed(1)}%`, label: 'Old chart matched' },
    { value: String(exactCodeCount), label: 'Same code' },
    { value: String(exactNameCount), label: 'Same name' },
    { value: String(fuzzyCount), label: 'Close matches' },
    { value: String(unmatchedCount), label: 'Still unmatched', warn: unmatchedCount > 0 },
  ];

  return (
    <div style={{ fontFamily: font.ui }}>
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
        {summaryCards.map(card => (
          <div
            key={card.label}
            style={{
              background: card.warn ? c.amber.fillSoft : c.card,
              border: `1px solid ${card.warn ? c.amber.border : c.border}`,
              borderRadius: 10,
              padding: '12px 14px',
            }}
          >
            <div style={{ fontFamily: font.mono, fontSize: 20, fontWeight: 800, color: card.warn ? c.amber.text2 : c.textPrimary }}>
              {card.value}
            </div>
            <div style={{ fontSize: 11, color: c.textSecondary, marginTop: 2 }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${c.border}`, marginBottom: 14, flexWrap: 'wrap' }}>
        {(Object.keys(TAB_LABELS) as TabKey[]).map(tab => {
          const active = activeTab === tab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              style={{
                background: 'transparent',
                border: 'none',
                borderBottom: active ? `3px solid ${c.textPrimary}` : '3px solid transparent',
                padding: '9px 12px',
                fontFamily: font.ui,
                fontSize: 12.5,
                fontWeight: active ? 800 : 600,
                color: active ? c.textPrimary : c.textSecondary,
                cursor: 'pointer',
              }}
            >
              {TAB_LABELS[tab]}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div style={{ ...panel, padding: 0, overflow: 'hidden' }}>
        {activeTab === 'segments' && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>In the old chart</th>
                <th style={thStyle}>In the new chart</th>
                <th style={thStyle}>How they line up</th>
                <th style={thStyle}>Score</th>
                <th style={thStyle}>Old codes</th>
                <th style={thStyle}>New codes</th>
                <th style={thStyle}>Codes shared</th>
              </tr>
            </thead>
            <tbody>
              {comparison.segment_comparison.map((seg, i) => (
                <tr key={i} style={seg.match_type === 'none' ? { background: c.amber.fillSoft } : undefined}>
                  <td style={{ ...tdStyle, fontWeight: 700, color: c.textPrimary }}>{seg.source_segment || '—'}</td>
                  <td style={{ ...tdStyle, fontWeight: 700, color: c.textPrimary }}>{seg.target_segment || '—'}</td>
                  <td style={tdStyle}>{matchBadge(seg.match_type)}</td>
                  <td style={{ ...tdStyle, fontFamily: font.mono, fontSize: 11.5 }}>{(seg.value_overlap * 100).toFixed(0)}%</td>
                  <td style={{ ...tdStyle, fontFamily: font.mono, fontSize: 11.5 }}>{seg.source_distinct_values}</td>
                  <td style={{ ...tdStyle, fontFamily: font.mono, fontSize: 11.5 }}>{seg.target_distinct_values}</td>
                  <td style={{ ...tdStyle, fontFamily: font.mono, fontSize: 11.5 }}>{(seg.value_overlap * 100).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {activeTab === 'hierarchy' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, padding: 20 }}>
            {[
              { label: 'Levels in the old chart', value: String(comparison.hierarchy_comparison.source_max_depth) },
              { label: 'Levels in the new chart', value: String(comparison.hierarchy_comparison.target_max_depth) },
              {
                label: 'Difference',
                value: `${comparison.hierarchy_comparison.depth_delta > 0 ? '+' : ''}${comparison.hierarchy_comparison.depth_delta}`,
                warn: comparison.hierarchy_comparison.depth_delta !== 0,
              },
              {
                label: 'What changed',
                value: comparison.hierarchy_comparison.depth_delta === 0
                  ? 'Same depth'
                  : comparison.hierarchy_comparison.depth_delta > 0
                    ? 'Deeper rollups in the new chart'
                    : 'Flatter rollups in the new chart',
              },
            ].map(stat => (
              <div key={stat.label} style={{ background: c.fill1, border: `1px solid ${c.hairline}`, borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, color: c.textSecondary, marginBottom: 4 }}>{stat.label}</div>
                <div style={{ fontFamily: font.mono, fontSize: 18, fontWeight: 800, color: (stat as any).warn ? c.amber.text2 : c.textPrimary }}>
                  {stat.value}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'attributes' && (
          comparison.attribute_gap.length === 0 ? (
            <div style={{ padding: 28, fontSize: 13, color: c.textSecondary }}>
              The new chart asks for nothing the old one doesn't already carry.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>New detail</th>
                  <th style={thStyle}>Must be filled in</th>
                  <th style={thStyle}>Needs a rule to fill it</th>
                </tr>
              </thead>
              <tbody>
                {comparison.attribute_gap.map((gap, i) => (
                  <tr key={i} style={!gap.present_in_source ? { background: c.amber.fillSoft } : undefined}>
                    <td style={{ ...tdStyle, fontWeight: 700, color: c.textPrimary }}>{gap.attribute}</td>
                    <td style={tdStyle}>{!gap.present_in_source ? 'Yes' : 'No'}</td>
                    <td style={tdStyle}>{gap.requires_population_rule ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {activeTab === 'types' && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Account type</th>
                <th style={thStyle}>In the old chart</th>
                <th style={thStyle}>In the new chart</th>
                <th style={thStyle}>Difference</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {comparison.account_type_alignment.map((t, i) => (
                <tr key={i}>
                  <td style={{ ...tdStyle, fontWeight: 700, color: c.textPrimary }}>{t.account_type}</td>
                  <td style={{ ...tdStyle, fontFamily: font.mono, fontSize: 11.5 }}>{t.source_count}</td>
                  <td style={{ ...tdStyle, fontFamily: font.mono, fontSize: 11.5 }}>{t.target_count}</td>
                  <td style={{ ...tdStyle, fontFamily: font.mono, fontSize: 11.5, color: t.delta !== 0 ? c.amber.text2 : c.textBody, fontWeight: t.delta !== 0 ? 700 : 400 }}>
                    {t.delta > 0 ? '+' : ''}{t.delta}
                  </td>
                  <td style={tdStyle}>
                    <span style={t.delta === 0 ? pill(c.green.fill, c.green.text) : pill(c.amber.fill, c.amber.text)}>
                      {t.delta === 0 ? 'aligned' : 'changed'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {activeTab === 'alignments' && (
          <div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Old account</th>
                  <th style={thStyle}>New account</th>
                  <th style={thStyle}>How it matched</th>
                  <th style={thStyle}>Similarity</th>
                </tr>
              </thead>
              <tbody>
                {comparison.candidate_alignments.slice(0, 50).map((a, i) => (
                  <tr key={i}>
                    <td style={{ ...tdStyle, fontFamily: font.mono, fontSize: 11.5, color: c.textSecondary }}>{a.source_code}</td>
                    <td style={{ ...tdStyle, fontFamily: font.mono, fontSize: 11.5, color: c.textSecondary }}>{a.target_code}</td>
                    <td style={tdStyle}>{matchBadge(a.match_method)}</td>
                    <td style={{ ...tdStyle, fontFamily: font.mono, fontSize: 11.5 }}>{(a.similarity_score * 100).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {comparison.candidate_alignments.length > 50 && (
              <p style={{ fontSize: 11.5, color: c.textTertiary, padding: '10px 14px', margin: 0 }}>
                Showing 50 of {comparison.candidate_alignments.length} matches
              </p>
            )}
          </div>
        )}
      </div>

      {/* Advance */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
        <button type="button" style={btnSecondary(running)} onClick={handleRunComparison} disabled={running}>
          {running ? 'Re-comparing…' : 'Re-run the comparison'}
        </button>
        <button
          type="button"
          style={{ ...btnPrimary(), fontSize: 14, padding: '11px 22px' }}
          onClick={onPhaseComplete}
        >
          Generate the account mapping →
        </button>
      </div>

      <Toast message={toast} onClose={() => setToast('')} />
    </div>
  );
};

export default StructuralComparison;
